import { useMutation } from '@tanstack/react-query';

import { hasKey } from '~/utils';
import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import {
  GetFinalOfferLetterPreviewRequestBody,
  GetFinalOfferLetterPreviewResponse,
  GetWaitlistLetterPreviewRequestBody,
  GetWaitlistLetterPreviewResponse,
  GetRejectionLetterPreviewResponse,
  GetRejectionLetterPreviewRequestBody,
  GetPreOfferLetterPreviewResponse,
  GetPreOfferLetterPreviewRequestBody,
} from '@ab/ams-school-api/types/private/v1';

export enum LetterPreviewType {
  FINAL_OFFER_LETTER_PREVIEW = 'final_offer_letter_preview',
  PRE_OFFER_LETTER_PREVIEW = 'pre_offer_letter_preview',
  WAITLIST_LETTER_PREVIEW = 'waitlist_letter_preview',
  REJECTION_LETTER_PREVIEW = 'rejection_letter_preview',
}

enum PayloadAttributeType {
  FINAL_OFFER = 'FINAL_OFFER',
  PRE_OFFER = 'PRE_OFFER',
  WAITLIST = 'WAITLIST',
  REJECTION = 'REJECTION',
}

export type FinalOfferLetterPreview = GetFinalOfferLetterPreviewResponse['data']['attributes'];
type GetFinalOfferLetterPreviewRequestPayload = Omit<
  GetFinalOfferLetterPreviewRequestBody['data']['attributes'],
  'type'
>;

export type WaitlistLetterPreview = GetWaitlistLetterPreviewResponse['data']['attributes'];
type GetWaitlistLetterPreviewRequestPayload = Omit<GetWaitlistLetterPreviewRequestBody['data']['attributes'], 'type'>;

export type RejectionLetterPreview = GetRejectionLetterPreviewResponse['data']['attributes'];
type GetRejectionLetterPreviewRequestPayload = Omit<GetRejectionLetterPreviewRequestBody['data']['attributes'], 'type'>;

export type PreOfferLetterPreview = GetPreOfferLetterPreviewResponse['data']['attributes'];
type GetPreOfferLetterPreviewRequestPayload = Omit<GetPreOfferLetterPreviewRequestBody['data']['attributes'], 'type'>;

export function useLetterPreview(applicationId: string, letterPreviewType: LetterPreviewType) {
  let payloadType: PayloadAttributeType;
  switch (letterPreviewType) {
    case LetterPreviewType.FINAL_OFFER_LETTER_PREVIEW:
      payloadType = PayloadAttributeType.FINAL_OFFER;
      break;
    case LetterPreviewType.PRE_OFFER_LETTER_PREVIEW:
      payloadType = PayloadAttributeType.PRE_OFFER;
      break;
    case LetterPreviewType.WAITLIST_LETTER_PREVIEW:
      payloadType = PayloadAttributeType.WAITLIST;
      break;
    case LetterPreviewType.REJECTION_LETTER_PREVIEW:
      payloadType = PayloadAttributeType.REJECTION;
      break;
    default:
      throw new Error(`Invalid letter preview type: ${letterPreviewType}`);
  }

  const request = useRequest();
  const { isLoading, error, mutateAsync } = useMutation(
    ['application_final_offer_letter_preview', applicationId],
    async (
      payload:
        | GetFinalOfferLetterPreviewRequestPayload
        | GetWaitlistLetterPreviewRequestPayload
        | GetRejectionLetterPreviewRequestPayload
        | GetPreOfferLetterPreviewRequestPayload
    ) => {
      const response = await request<
        | GetFinalOfferLetterPreviewResponse
        | GetWaitlistLetterPreviewResponse
        | GetRejectionLetterPreviewResponse
        | GetPreOfferLetterPreviewResponse
      >(`${config.apiHost}/private/v1/applications/${applicationId}/${letterPreviewType}`, {
        method: 'POST',
        body: {
          data: {
            type: 'letter_preview',
            attributes: { ...payload, type: payloadType },
          },
        } as
          | GetFinalOfferLetterPreviewRequestBody
          | GetWaitlistLetterPreviewRequestBody
          | GetRejectionLetterPreviewRequestBody
          | GetPreOfferLetterPreviewRequestBody,
        isExpectedResponse,
      });

      const attributes = response.data.attributes;
      return {
        ...attributes,
      };
    }
  );
  return {
    generateLetterPreview: mutateAsync,
    isGeneratingLetterPreview: isLoading,
    letterPreviewGenerationError: error,
  };
}

function isExpectedResponse(
  res: unknown
): res is
  | GetFinalOfferLetterPreviewResponse
  | GetWaitlistLetterPreviewResponse
  | GetRejectionLetterPreviewResponse
  | GetPreOfferLetterPreviewResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
